import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  FileText, 
  Plus, 
  Trash2, 
  Save, 
  ArrowLeft,
  Loader2,
  AlertCircle,
  Info,
  Users
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Complete SAP-Standard Request for Quotation (RFQ) Creation Form
 * Organized in tabs: Header | Item Details | Vendor Assignment
 */
const RFQCreateComplete = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMasterData, setLoadingMasterData] = useState(true);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  
  // Master data
  const [masterData, setMasterData] = useState({
    documentTypes: [],
    itemCategories: [],
    materialGroups: [],
    purchasingOrgs: [],
    purchasingGroups: [],
    companyCodes: [],
    plants: [],
    storageLocations: [],
    materials: [],
    vendors: [],
    languages: [
      { code: 'EN', name: 'English' },
      { code: 'DE', name: 'German' },
      { code: 'FR', name: 'French' },
      { code: 'ES', name: 'Spanish' },
      { code: 'IT', name: 'Italian' }
    ],
    deliveryDateCategories: [
      { id: 'D', name: 'Day' },
      { id: 'W', name: 'Week' },
      { id: 'M', name: 'Month' }
    ]
  });

  // Form data - Header fields
  const [formData, setFormData] = useState({
    document_type: '',
    language: 'EN',
    rfq_date: new Date().toISOString().split('T')[0],
    quotation_deadline: '',
    purchasing_organization: '',
    purchasing_group: '',
    company_code: '',
    collective_rfq_number: '',
    description: '',
    reference_number: '',
    items: []
  });
  
  // Store material-specific plant data for filtering
  const [materialPlants, setMaterialPlants] = useState({});

  // Item template
  const createNewItem = (itemNumber) => ({
    item_number: itemNumber,
    item_category: '',
    material_id: '',
    short_text: '',
    quantity: '',
    unit_of_measure: 'EA',
    delivery_date: '',
    delivery_date_category: 'D',
    plant: '',
    storage_location: '',
    material_group: '',
    vendors: [],
    pr_number: '',
    pr_item: null,
    target_price: '',
    info_text: ''
  });

  useEffect(() => {
    loadMasterData();
    // Initialize with one item
    setFormData(prev => ({
      ...prev,
      items: [createNewItem(10)]
    }));
  }, []);

  const loadMasterData = async () => {
    try {
      setLoadingMasterData(true);
      
      const [
        docTypesRes,
        itemCategoriesRes,
        materialGroupsRes,
        purchOrgRes,
        purchGroupRes,
        companyRes,
        plantRes,
        slocRes,
        materialRes,
        vendorRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/p2p/document-types/`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sap/item-categories`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sap/material-groups`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-groups`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/company/company-codes`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/storage-locations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendors`).catch(() => ({ data: [] }))
      ]);

      setMasterData(prev => ({
        ...prev,
        documentTypes: docTypesRes.data,
        itemCategories: itemCategoriesRes.data,
        materialGroups: materialGroupsRes.data,
        purchasingOrgs: purchOrgRes.data,
        purchasingGroups: purchGroupRes.data,
        companyCodes: companyRes.data,
        plants: plantRes.data,
        storageLocations: slocRes.data,
        materials: materialRes.data,
        vendors: vendorRes.data
      }));
      
      // Don't auto-select - let user choose
      setLoadingMasterData(false);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Error loading master data');
      setLoadingMasterData(false);
    }
  };

  const handleHeaderChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = async (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto-populate fields when material is selected
    if (field === 'material_id' && value) {
      try {
        console.log(`Fetching details for material: ${value}`);
        const response = await axios.get(`${BACKEND_URL}/api/materials/${value}/details`);
        const materialData = response.data;
        
        console.log('Material data received:', materialData);
        
        // Auto-populate basic material fields
        if (materialData.description) {
          newItems[index].short_text = materialData.description.substring(0, 40);
          console.log('Populated short_text:', materialData.description);
        }
        if (materialData.base_uom) {
          newItems[index].unit_of_measure = materialData.base_uom;
          console.log('Populated UOM:', materialData.base_uom);
        }
        if (materialData.material_group) {
          newItems[index].material_group = materialData.material_group;
          console.log('Populated material_group:', materialData.material_group);
        }
        
        // Store material plants for filtering
        setMaterialPlants(prev => ({
          ...prev,
          [index]: materialData.plants || []
        }));
        
        // Handle plant extensions
        if (materialData.plants && materialData.plants.length > 0) {
          if (materialData.plants.length === 1) {
            // Single plant - auto-populate
            const plantData = materialData.plants[0];
            newItems[index].plant = plantData.plant_id;
            console.log('Auto-selected plant:', plantData.plant_id);
            
            if (plantData.storage_location) {
              newItems[index].storage_location = plantData.storage_location;
              console.log('Populated storage_location:', plantData.storage_location);
            }
            
            toast.success(`Material loaded! Plant ${plantData.plant_id} auto-selected.`);
          } else {
            console.log(`Material has ${materialData.plants.length} plants:`, materialData.plants.map(p => p.plant_id));
            toast.success(`Material ${value} has ${materialData.plants.length} plants. Please select plant manually.`, {
              duration: 4000
            });
          }
        } else {
          toast.error(`Material ${value} has no plant extensions. Please select plant manually.`);
        }
        
      } catch (error) {
        console.error('Error fetching material details:', error);
        if (error.response?.status === 404) {
          toast.error(`Material ${value} not found in system`);
        } else {
          toast.error(`Error loading material details: ${error.message}`);
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const addItem = () => {
    const lastItem = formData.items[formData.items.length - 1];
    const newItemNumber = lastItem ? lastItem.item_number + 10 : 10;
    const newItem = createNewItem(newItemNumber);
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setActiveItemIndex(formData.items.length);
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error('Cannot remove the last item');
      return;
    }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
    if (activeItemIndex >= newItems.length) {
      setActiveItemIndex(newItems.length - 1);
    }
  };

  const addVendorToItem = (itemIndex, vendorNumber) => {
    if (!vendorNumber) return;
    
    const newItems = [...formData.items];
    const item = newItems[itemIndex];
    
    // Check if vendor already exists
    const vendorExists = item.vendors.some(v => v.vendor_number === vendorNumber);
    if (vendorExists) {
      toast.error('Vendor already added to this item');
      return;
    }
    
    // Add vendor
    item.vendors.push({
      vendor_number: vendorNumber,
      quotation_status: 'A',  // Awaiting
      quoted_price: null,
      quoted_currency: 'USD',
      quoted_delivery_date: null,
      response_date: null,
      remarks: null
    });
    
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
    
    toast.success('Vendor added successfully');
  };

  const removeVendorFromItem = (itemIndex, vendorIndex) => {
    const newItems = [...formData.items];
    newItems[itemIndex].vendors.splice(vendorIndex, 1);
    
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const validateForm = () => {
    // Header validations
    if (!formData.document_type) {
      toast.error('Document Type is required');
      return false;
    }

    if (!formData.quotation_deadline) {
      toast.error('Quotation Deadline is required');
      return false;
    }

    // Check deadline is future date
    const deadline = new Date(formData.quotation_deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (deadline <= today) {
      toast.error('Quotation Deadline must be a future date');
      return false;
    }

    if (!formData.purchasing_organization) {
      toast.error('Purchasing Organization is required');
      return false;
    }

    if (!formData.purchasing_group) {
      toast.error('Purchasing Group is required');
      return false;
    }

    if (!formData.company_code) {
      toast.error('Company Code is required');
      return false;
    }

    // Items validation
    if (formData.items.length === 0) {
      toast.error('At least one item is required');
      return false;
    }

    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      const itemNum = item.item_number;

      if (!item.short_text.trim()) {
        toast.error(`Item ${itemNum}: Short text is required`);
        return false;
      }

      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`Item ${itemNum}: Quantity must be greater than 0`);
        return false;
      }

      if (!item.unit_of_measure) {
        toast.error(`Item ${itemNum}: Unit of Measure is required`);
        return false;
      }

      if (!item.delivery_date) {
        toast.error(`Item ${itemNum}: Delivery date is required`);
        return false;
      }

      if (!item.plant) {
        toast.error(`Item ${itemNum}: Plant is required`);
        return false;
      }

      // At least one vendor required per item
      if (item.vendors.length === 0) {
        toast.error(`Item ${itemNum}: At least one vendor must be assigned`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Clean up the data before submission
      const cleanedItems = formData.items.map(item => {
        const cleanedItem = { ...item };
        
        // Convert empty strings to null for optional fields
        ['material_id', 'storage_location', 'material_group', 'pr_number', 'info_text'].forEach(field => {
          if (cleanedItem[field] === '') {
            cleanedItem[field] = null;
          }
        });
        
        // Convert empty strings to null for optional number fields
        ['pr_item', 'target_price'].forEach(field => {
          if (cleanedItem[field] === '' || cleanedItem[field] === undefined) {
            cleanedItem[field] = null;
          }
        });
        
        return cleanedItem;
      });
      
      const cleanedFormData = {
        ...formData,
        items: cleanedItems,
        collective_rfq_number: formData.collective_rfq_number || null,
        description: formData.description || null,
        reference_number: formData.reference_number || null
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/rfqs`, cleanedFormData);
      toast.success(`RFQ ${response.data.rfq_number} created successfully!`);
      navigate(`/p2p/rfq-view/${response.data.rfq_number}`);
    } catch (error) {
      console.error('Error creating RFQ:', error);
      const errorDetail = error.response?.data?.detail;
      
      let errorMsg = 'Error creating RFQ';
      if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMsg = errorDetail.map(err => {
          if (typeof err === 'object' && err.msg) {
            const field = err.loc ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          }
          return String(err);
        }).join(', ');
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        errorMsg = JSON.stringify(errorDetail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingMasterData) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-3 text-lg">Loading master data...</span>
        </div>
      </Layout>
    );
  }

  const currentItem = formData.items[activeItemIndex];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/p2p/rfq-list')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Create Request for Quotation
              </h1>
              <p className="text-sm text-gray-500">Complete all required fields across all tabs</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="header">RFQ Header</TabsTrigger>
              <TabsTrigger value="items">Item Details</TabsTrigger>
              <TabsTrigger value="vendors">Vendor Assignment</TabsTrigger>
            </TabsList>

            {/* ===================== TAB 1: RFQ HEADER ===================== */}
            <TabsContent value="header">
              <Card>
                <CardHeader>
                  <CardTitle>RFQ Header - General Information</CardTitle>
                  <CardDescription>Document control and organizational details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* Document Type */}
                    <div>
                      <Label>Document Type *</Label>
                      <select
                        value={formData.document_type}
                        onChange={(e) => handleHeaderChange('document_type', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        required
                      >
                        <option value="">Select Document Type</option>
                        {masterData.documentTypes.map(dt => (
                          <option key={`${dt.document_type_id}-${dt.category}`} value={dt.document_type_id}>
                            {dt.document_type_id} - {dt.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Language */}
                    <div>
                      <Label>Language *</Label>
                      <select
                        value={formData.language}
                        onChange={(e) => handleHeaderChange('language', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        required
                      >
                        {masterData.languages.map(lang => (
                          <option key={lang.code} value={lang.code}>
                            {lang.code} - {lang.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* RFQ Date */}
                    <div>
                      <Label>RFQ Date *</Label>
                      <Input
                        type="date"
                        value={formData.rfq_date}
                        onChange={(e) => handleHeaderChange('rfq_date', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    {/* Quotation Deadline */}
                    <div>
                      <Label>Quotation Deadline *</Label>
                      <Input
                        type="date"
                        value={formData.quotation_deadline}
                        onChange={(e) => handleHeaderChange('quotation_deadline', e.target.value)}
                        min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    {/* Purchasing Organization */}
                    <div>
                      <Label>Purchasing Organization *</Label>
                      <select
                        value={formData.purchasing_organization}
                        onChange={(e) => handleHeaderChange('purchasing_organization', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        required
                      >
                        <option value="">Select</option>
                        {masterData.purchasingOrgs.map(po => (
                          <option key={po.purchasing_org_id} value={po.purchasing_org_id}>
                            {po.purchasing_org_id} - {po.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Purchasing Group */}
                    <div>
                      <Label>Purchasing Group *</Label>
                      <select
                        value={formData.purchasing_group}
                        onChange={(e) => handleHeaderChange('purchasing_group', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        required
                      >
                        <option value="">Select</option>
                        {masterData.purchasingGroups.map(pg => (
                          <option key={pg.purchasing_group_id} value={pg.purchasing_group_id}>
                            {pg.purchasing_group_id} - {pg.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Company Code */}
                    <div>
                      <Label>Company Code *</Label>
                      <select
                        value={formData.company_code}
                        onChange={(e) => handleHeaderChange('company_code', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        required
                      >
                        <option value="">Select Company Code</option>
                        {masterData.companyCodes.map(cc => (
                          <option key={cc.company_code_id} value={cc.company_code_id}>
                            {cc.company_code_id} - {cc.company_code_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Collective RFQ Number */}
                    <div>
                      <Label>Collective RFQ Number</Label>
                      <Input
                        value={formData.collective_rfq_number}
                        onChange={(e) => handleHeaderChange('collective_rfq_number', e.target.value)}
                        maxLength={10}
                        placeholder="Optional reference"
                      />
                    </div>

                    {/* Description */}
                    <div className="md:col-span-2">
                      <Label>Description</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => handleHeaderChange('description', e.target.value)}
                        maxLength={100}
                        placeholder="RFQ description"
                      />
                    </div>

                    {/* Reference Number */}
                    <div>
                      <Label>Reference Number</Label>
                      <Input
                        value={formData.reference_number}
                        onChange={(e) => handleHeaderChange('reference_number', e.target.value)}
                        maxLength={20}
                        placeholder="External reference"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===================== TAB 2: ITEM DETAILS ===================== */}
            <TabsContent value="items">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>RFQ Line Items</CardTitle>
                      <CardDescription>Item details and specifications</CardDescription>
                    </div>
                    <Button type="button" onClick={addItem} size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Item Selector */}
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {formData.items.map((item, index) => (
                      <Button
                        key={`item-tab-${item.item_number || index}`}
                        type="button"
                        variant={activeItemIndex === index ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveItemIndex(index)}
                        className="relative"
                      >
                        Item {item.item_number}
                        {formData.items.length > 1 && (
                          <Trash2
                            className="h-3 w-3 ml-2 cursor-pointer hover:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeItem(index);
                            }}
                          />
                        )}
                      </Button>
                    ))}
                  </div>

                  {currentItem && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                      <div className="md:col-span-3">
                        <div className="bg-orange-50 p-3 rounded-md flex items-start gap-2">
                          <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-orange-800">
                            Editing Item {currentItem.item_number}. Vendors can be assigned in the "Vendor Assignment" tab.
                          </p>
                        </div>
                      </div>

                      {/* Item Number (Read-only) */}
                      <div>
                        <Label>Item Number</Label>
                        <Input
                          value={currentItem.item_number}
                          readOnly
                          className="bg-gray-100"
                        />
                      </div>

                      {/* Item Category */}
                      <div>
                        <Label>Item Category</Label>
                        <select
                          value={currentItem.item_category}
                          onChange={(e) => handleItemChange(activeItemIndex, 'item_category', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          {masterData.itemCategories.map(ic => (
                            <option key={ic.item_category_id || 'standard'} value={ic.item_category_id}>
                              {ic.item_category_id ? `${ic.item_category_id} - ${ic.description}` : ic.description}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Material */}
                      <div>
                        <Label>Material</Label>
                        <select
                          value={currentItem.material_id}
                          onChange={(e) => handleItemChange(activeItemIndex, 'material_id', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">Select Material</option>
                          {masterData.materials.map(mat => (
                            <option key={mat.material_number} value={mat.material_number}>
                              {mat.material_number} - {mat.description}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Short Text */}
                      <div className="md:col-span-2">
                        <Label>Short Text *</Label>
                        <Input
                          value={currentItem.short_text}
                          onChange={(e) => handleItemChange(activeItemIndex, 'short_text', e.target.value)}
                          maxLength={40}
                          placeholder="Item description"
                          required
                        />
                      </div>

                      {/* Quantity */}
                      <div>
                        <Label>Quantity *</Label>
                        <Input
                          type="number"
                          step="0.001"
                          min="0.001"
                          value={currentItem.quantity}
                          onChange={(e) => handleItemChange(activeItemIndex, 'quantity', e.target.value)}
                          placeholder="0.000"
                          required
                        />
                      </div>

                      {/* Unit of Measure */}
                      <div>
                        <Label>Unit of Measure *</Label>
                        <Input
                          value={currentItem.unit_of_measure}
                          onChange={(e) => handleItemChange(activeItemIndex, 'unit_of_measure', e.target.value)}
                          maxLength={3}
                          placeholder="EA"
                          required
                        />
                      </div>

                      {/* Delivery Date */}
                      <div>
                        <Label>Delivery Date *</Label>
                        <Input
                          type="date"
                          value={currentItem.delivery_date}
                          onChange={(e) => handleItemChange(activeItemIndex, 'delivery_date', e.target.value)}
                          required
                        />
                      </div>

                      {/* Delivery Date Category */}
                      <div>
                        <Label>Delivery Date Category</Label>
                        <select
                          value={currentItem.delivery_date_category}
                          onChange={(e) => handleItemChange(activeItemIndex, 'delivery_date_category', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          {masterData.deliveryDateCategories.map(ddc => (
                            <option key={ddc.id} value={ddc.id}>
                              {ddc.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Plant */}
                      <div>
                        <Label>Plant *</Label>
                        <select
                          value={currentItem.plant}
                          onChange={(e) => handleItemChange(activeItemIndex, 'plant', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          required
                        >
                          <option value="">Select Plant</option>
                          {(() => {
                            const materialPlantsForItem = materialPlants[activeItemIndex] || [];
                            if (currentItem.material_id && materialPlantsForItem.length > 0) {
                              const plantIds = materialPlantsForItem.map(p => p.plant_id);
                              return masterData.plants
                                .filter(plant => plantIds.includes(plant.plant_id))
                                .map(plant => (
                                  <option key={plant.plant_id} value={plant.plant_id}>
                                    {plant.plant_id} - {plant.plant_name}
                                  </option>
                                ));
                            }
                            return masterData.plants.map(plant => (
                              <option key={plant.plant_id} value={plant.plant_id}>
                                {plant.plant_id} - {plant.plant_name}
                              </option>
                            ));
                          })()}
                        </select>
                      </div>

                      {/* Storage Location */}
                      <div>
                        <Label>Storage Location</Label>
                        <select
                          value={currentItem.storage_location}
                          onChange={(e) => handleItemChange(activeItemIndex, 'storage_location', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">Select Storage Location</option>
                          {masterData.storageLocations
                            ?.filter(sl => !currentItem.plant || sl.plant_id === currentItem.plant)
                            .map(sloc => (
                              <option key={sloc.storage_location_id} value={sloc.storage_location_id}>
                                {sloc.storage_location_id} - {sloc.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Material Group */}
                      <div>
                        <Label>Material Group</Label>
                        <select
                          value={currentItem.material_group}
                          onChange={(e) => handleItemChange(activeItemIndex, 'material_group', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">Select Material Group</option>
                          {masterData.materialGroups.map(mg => (
                            <option key={mg.material_group_id} value={mg.material_group_id}>
                              {mg.material_group_id} - {mg.description}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Target Price */}
                      <div>
                        <Label>Target Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={currentItem.target_price}
                          onChange={(e) => handleItemChange(activeItemIndex, 'target_price', e.target.value)}
                          placeholder="0.00"
                        />
                      </div>

                      {/* PR Reference (if applicable) */}
                      <div>
                        <Label>PR Number</Label>
                        <Input
                          value={currentItem.pr_number}
                          onChange={(e) => handleItemChange(activeItemIndex, 'pr_number', e.target.value)}
                          maxLength={10}
                          placeholder="Optional PR reference"
                        />
                      </div>

                      <div>
                        <Label>PR Item</Label>
                        <Input
                          type="number"
                          value={currentItem.pr_item || ''}
                          onChange={(e) => handleItemChange(activeItemIndex, 'pr_item', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="Optional"
                        />
                      </div>

                      {/* Info Text */}
                      <div className="md:col-span-3">
                        <Label>Info Text</Label>
                        <textarea
                          value={currentItem.info_text}
                          onChange={(e) => handleItemChange(activeItemIndex, 'info_text', e.target.value)}
                          maxLength={500}
                          rows={3}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="Additional information for this item"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===================== TAB 3: VENDOR ASSIGNMENT ===================== */}
            <TabsContent value="vendors">
              <Card>
                <CardHeader>
                  <CardTitle>Vendor Assignment</CardTitle>
                  <CardDescription>Assign vendors to each item (at least one vendor required per item)</CardDescription>
                </CardHeader>
                <CardContent>
                  {formData.items.map((item, itemIndex) => (
                    <div key={`vendor-assign-${item.item_number || itemIndex}`} className="mb-6 pb-6 border-b last:border-b-0">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-lg">
                          Item {item.item_number}: {item.short_text || 'No description'}
                        </h3>
                        <span className="text-sm text-gray-600">
                          {item.vendors.length} vendor(s) assigned
                        </span>
                      </div>

                      {/* Add Vendor */}
                      <div className="flex gap-2 mb-3">
                        <select
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          onChange={(e) => {
                            if (e.target.value) {
                              addVendorToItem(itemIndex, e.target.value);
                              e.target.value = '';
                            }
                          }}
                        >
                          <option value="">Select vendor to add...</option>
                          {masterData.vendors.map(vendor => (
                            <option key={vendor.vendor_number} value={vendor.vendor_number}>
                              {vendor.vendor_number} - {vendor.name}
                            </option>
                          ))}
                        </select>
                        <Button type="button" variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>

                      {/* Assigned Vendors */}
                      {item.vendors.length > 0 ? (
                        <div className="space-y-2">
                          {item.vendors.map((vendor, vendorIndex) => {
                            const vendorData = masterData.vendors.find(v => v.vendor_number === vendor.vendor_number);
                            return (
                              <div key={`${item.item_number || itemIndex}-${vendor.vendor_number || vendorIndex}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                <div className="flex items-center gap-3">
                                  <Users className="h-5 w-5 text-gray-500" />
                                  <div>
                                    <p className="font-medium">{vendor.vendor_number}</p>
                                    <p className="text-sm text-gray-600">{vendorData?.name || 'Unknown vendor'}</p>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeVendorFromItem(itemIndex, vendorIndex)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center p-6 bg-yellow-50 rounded-md">
                          <AlertCircle className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                          <p className="text-sm text-yellow-800">No vendors assigned to this item yet</p>
                          <p className="text-xs text-yellow-600 mt-1">At least one vendor is required</p>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/p2p/rfq-list')}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating RFQ...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create RFQ
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default RFQCreateComplete;
